import { useEffect, useRef, useState } from "react";

// The next local midnight after `now`: when a time shown as today's (formatWhen)
// becomes an earlier day's.
export function nextLocalMidnight(now: number): number {
  const d = new Date(now);
  d.setHours(24, 0, 0, 0);
  return d.getTime();
}

// The clock a page renders time-derived text from: a period's start, a
// session's "live" status, whether a time is today's. React renders only when
// data or state changes, so without a clock such text goes stale while the
// page stays open. `nextChangeAt(now)` names the next instant something shown
// would change (or null for never), and the clock is read again then.
//
// A timer's delay does not count time the machine sleeps, so after a night
// with the lid closed it fires hours late. The clock is therefore also read
// when the page becomes visible or the window gains focus, and kept as it is
// when nothing shown would have changed yet, so nothing renders again.
export function useNow(nextChangeAt: (now: number) => number | null): number {
  const [now, setNow] = useState(() => Date.now());
  const next = nextChangeAt(now);
  // A time already past changes nothing it has not already changed.
  const due = next !== null && next > now ? next : null;
  const dueRef = useRef(due);
  useEffect(() => {
    dueRef.current = due;
  });
  useEffect(() => {
    if (due === null) return;
    const timer = setTimeout(() => setNow(Date.now()), due - Date.now());
    return () => clearTimeout(timer);
  }, [due, now]);
  useEffect(() => {
    const reread = () => {
      const t = Date.now();
      const at = dueRef.current;
      if (at !== null && t >= at) setNow(t);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") reread();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", reread);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", reread);
    };
  }, []);
  return now;
}
